import { createMessage } from "helpers";

/**
 * Serialize a store for the cart `attributes.store` value.
 * @param {Object} store
 * @returns {string|null}
 */
function serializeStoreForCart(store) {
  if (!store) {
    return null;
  }

  return JSON.stringify({
    id: store.id,
    name: store.name,
    handle: store.handle,
  });
}

/**
 * Format a store object for analytics events.
 * @param {Object} store
 * @param {string} [selectionMethod] - e.g. `"auto"` | `"manual"`
 * @returns {Object|null}
 */
function formatStoreAnalyticsPayload(store, selectionMethod) {
  if (!store) {
    return null;
  }

  if (!selectionMethod) {
    return { ...store };
  }

  return {
    ...store,
    selection_method: selectionMethod,
  };
}

class PreferredStores {
  constructor() {
    if (this.userPreferredStore) {
      this.hydrateExistingStore(this.userPreferredStore);
      return;
    }

    WYATT.PubSub.subscribe(
      WYATT.PubSub.events.userLocationSet,
      this.setClosestStoreAsPreferred.bind(this),
    );
  }

  get userPreferredStore() {
    const stored = window.localStorage.getItem(
      WYATT.config.preferredStore.localStorageKey,
    );

    if (!stored) {
      return null;
    }

    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  get cartPreferredStore() {
    const raw =
      WYATT.Cart?.data?.attributes?.store ??
      WYATT.shopify.cart?.attributes?.store;

    if (!raw) {
      return {};
    }

    try {
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch {
      return {};
    }
  }

  /**
   * Cart attribute string for the current preferred store, or null.
   * @returns {string|null}
   */
  serializeStore(store = this.store) {
    return serializeStoreForCart(store);
  }

  /**
   * Cart-first sync for a store already known from localStorage.
   * @param {Object} store
   */
  async hydrateExistingStore(store) {
    const ok = await this.persist(store);

    if (!ok) {
      return;
    }

    this.store = store;

    WYATT.PubSub.publish(WYATT.PubSub.events.preferredStoreSet, store);

    WYATT.Analytics.publish(WYATT.Analytics.events.page_context, {
      selected_store: formatStoreAnalyticsPayload(store),
    });
  }

  /**
   * Cart-first sync. On cart failure, show a shopper message and skip
   * localStorage writes.
   * @param {Object} store
   * @returns {Promise<boolean>}
   */
  async persist(store) {
    const ok = await this.setOnCart(store);

    if (!ok) {
      createMessage(WYATT.t.preferred_store.unable_to_set, { type: "error" });
      return false;
    }

    this.setOnUser(store);

    return true;
  }

  async setClosestStoreAsPreferred() {
    if (this.store) {
      return;
    }

    if (!WYATT.Geolocation.sortedStores) {
      return;
    }

    const [store] = WYATT.Geolocation.sortedStores;

    const ok = await this.persist(store);

    if (!ok) {
      return;
    }

    WYATT.Analytics.publish(WYATT.Analytics.events.select_store, {
      previous_store: formatStoreAnalyticsPayload(this.store, "auto"),
      selected_store: formatStoreAnalyticsPayload(store, "auto"),
    });

    this.store = store;

    WYATT.PubSub.publish(WYATT.PubSub.events.preferredStoreSet, store);
  }

  async setStore(store) {
    const previousStore = this.store;
    const ok = await this.persist(store);

    if (!ok) {
      return;
    }

    WYATT.Analytics.publish(WYATT.Analytics.events.select_store, {
      previous_store: formatStoreAnalyticsPayload(previousStore, "manual"),
      selected_store: formatStoreAnalyticsPayload(store, "manual"),
    });

    this.store = store;

    WYATT.PubSub.publish(WYATT.PubSub.events.preferredStoreSet, store);
  }

  setOnUser(store) {
    const serializedStore = JSON.stringify(store);

    if (
      serializedStore ===
      window.localStorage.getItem(WYATT.config.preferredStore.localStorageKey)
    ) {
      return;
    }

    window.localStorage.setItem(
      WYATT.config.preferredStore.localStorageKey,
      serializedStore,
    );
  }

  /**
   * @param {Object} store
   * @returns {Promise<boolean>}
   */
  async setOnCart(store) {
    const storeAttribute = serializeStoreForCart(store);

    if (this.cartPreferredStore.id === store.id) {
      return true;
    }

    const result = await WYATT.Cart.update({
      attributes: {
        store: storeAttribute,
      },
    });

    return Boolean(result);
  }
}

WYATT.PreferredStores = new PreferredStores();

export default PreferredStores;
